import { type FC, type ReactNode } from "react";
import { useInView } from "react-intersection-observer";

import { cn, getUniqueId } from "@btnet/utilities-core";
import { useWebviewContext } from "@btnet/utilities-webview";
import { useIsMobileViewportWebview } from "@btnet/utilities-webview/useIsMobileViewportWebview";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { isInIframe } from "utilities/url/url";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTIconCloseOutlined } from "commonComponents/btWrappers/BTIcon";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import {
    EntityShareButtonBDS,
    type ISharingConfig,
} from "commonComponents/entity/sharing/EntityShareButton/EntityShareButton";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";

import "./BTModalLayout.less";

export interface IBTModalLayoutProps {
    title?: ReactNode;
    /**
     * Id for the title. Pass the same id to the BTModal as
     * `wrapProps={{ "aria-labelledby": titleId }}` so screen readers name the dialog by its title.
     */
    titleId?: string;
    modalHeaderClassName?: string;
    subtitle?: ReactNode;
    sharing?: ISharingConfig;
    footerContent?: ReactNode;
    modalConfig?: IModalConfiguration;
    className?: string;
    /**
     * Controls whether the close button and click-outside-to-close mask are enabled.
     * The mask is hidden in any webview context regardless of this value, and the close button is
     * hidden in a phone-width webview unless `showCloseInWebview` is set.
     * @default true
     */
    closable?: boolean;
    /**
     * Keeps the header close button visible in a phone-width webview.
     *
     * There the modal covers the screen and native back is the dismissal affordance, so an X is
     * redundant. Set this on modals opened from within another webview screen, where there is no
     * native close for the modal itself and hiding it leaves no way out.
     * @default false
     */
    showCloseInWebview?: boolean;
    headerLogoAlign?: "center" | "left";
    children?: ReactNode;
    testId?: string;
    additionalHeaderContent?: ReactNode;
    headerBottom?: ReactNode;
    layoutRef?: React.RefObject<HTMLDivElement>;
    headerRef?: React.RefObject<HTMLDivElement>;
}

export const BTModalLayout: FC<IBTModalLayoutProps & ITrackingProp> = track({})((props) => {
    const webviewContext = useWebviewContext();
    // Set initialInView to true to avoid a flash on the modal https://github.com/thebuilder/react-intersection-observer/issues/499
    const { ref: topRef, inView: topInView } = useInView({ threshold: 1, initialInView: true });
    const { ref: bottomRef, inView: bottomInView } = useInView({
        threshold: 1,
        initialInView: true,
    });

    const content = (
        <div
            className={cn("BTModalLayout", props.className)}
            data-testid={props.testId ?? "modalLayout"}
            ref={props.layoutRef}
        >
            {props.title && (
                <BTModalHeader
                    {...props}
                    topInView={topInView}
                    className={cn(props.modalHeaderClassName)}
                />
            )}
            <div
                className="ModalContentContainer"
                data-testid={props.testId ? `${props.testId}-content` : "modalContent"}
            >
                <div ref={topRef} />
                {props.children}
                <div ref={bottomRef} />
            </div>
            {props.footerContent && (
                <div
                    className={cn("BTModalFooter", {
                        Unstuck: !bottomInView,
                    })}
                    data-testid={props.testId ? `${props.testId}-footer` : "modalFooter"}
                >
                    {props.footerContent}
                </div>
            )}
            {props.closable !== false && !webviewContext && props.modalConfig?.beforeClose && (
                <div
                    className="BTModalMask"
                    onClick={() => {
                        props.modalConfig?.beforeClose();
                        props.tracking?.trackEvent({
                            event: "ModalClose",
                            extraInfo: {
                                isFromClickOut: true,
                            },
                        });
                    }}
                />
            )}
        </div>
    );
    return isInIframe() ? <FocusProvider>{content}</FocusProvider> : content;
});

export const BTModalHeader: FC<IBTModalLayoutProps & { topInView?: boolean }> = ({
    className,
    closable = true,
    modalConfig,
    sharing,
    subtitle,
    headerLogoAlign = "center",
    title,
    titleId,
    testId,
    additionalHeaderContent,
    headerRef,
    headerBottom,
    topInView,
    showCloseInWebview = false,
}) => {
    // Width-aware, unlike the mask's `useWebviewContext()` check below: a tablet-width webview
    // still renders this as a floating card with room for its own X, and dropping it there would
    // leave no way out.
    const isMobileViewportWebview = useIsMobileViewportWebview();
    return (
        <div
            className={cn("BTModalHeader", { Unstuck: !topInView })}
            data-testid={testId ? `${testId}-header` : "modalHeader"}
            ref={headerRef}
        >
            <div className={cn("Top", className)}>
                <div
                    className={cn("ModalHeaderContainer flex-grow-1 min-w-0", {
                        "ModalHeaderContainer--left": headerLogoAlign === "left",
                    })}
                >
                    <div className="ModalHeaderContent min-w-0">
                        {subtitle && (
                            // The 12px line is shorter than the font, so the padding (cancelled by
                            // the negative margin) keeps accents inside the truncate clip.
                            <div className="ModalHeaderJobName truncate text-xs leading-3 py-0.5 -my-0.5 min-h-5">
                                {subtitle}
                            </div>
                        )}
                        {title && (
                            <span className="ShareableTitle">
                                <BTTitle id={titleId} level={2} bold className="ModalHeaderTitle">
                                    {title}
                                </BTTitle>

                                <EntityShareButtonBDS
                                    id={`entityShareButtonBds${getUniqueId("string")}`}
                                    sharing={sharing}
                                />
                            </span>
                        )}
                    </div>
                </div>
                {additionalHeaderContent}

                {closable && (!isMobileViewportWebview || showCloseInWebview) && (
                    <BTButtonDeprecated
                        type="tertiary"
                        isolated
                        data-testid="close"
                        hotkey={{ hotkey: "close", popoverPlacement: "bottom" }}
                        onClick={modalConfig?.beforeClose}
                        className="ModalHeaderClose"
                        icon={
                            <BTIconCloseOutlined
                                title="Close"
                                size={20}
                                className="ModalHeaderCloseIcon"
                            />
                        }
                    />
                )}
            </div>
            {headerBottom && (
                <BTRow className="BTModalHeader-Bottom">
                    <BTCol span={24}>{headerBottom}</BTCol>
                </BTRow>
            )}
        </div>
    );
};
